// -----------------------------------------------------------
// FloraIQ — Drawer de Detalhe da Inconsistência (dado real)
// -----------------------------------------------------------

import type { Inconsistency } from "@/types/ui-models";
import { QUALITY_RULES_BY_CODE, QUALITY_RULE_GROUP_LABELS } from "@/config/qualidade-3d-config";
import { SeverityBadge, ValidationStatusBadge } from "./QualityStatusBadge";
import { formatDetectedAt, projectLabel } from "./qualityFormat";

interface InconsistencyDetailDrawerProps {
  issue: Inconsistency | null;
  projectNames: Record<string, string>;
  onClose: () => void;
}

export default function InconsistencyDetailDrawer({ issue, projectNames, onClose }: InconsistencyDetailDrawerProps) {
  if (!issue) return null;

  const rule = QUALITY_RULES_BY_CODE[issue.regra];

  const info: { label: string; value: string; mono?: boolean }[] = [
    { label: "Código da regra", value: issue.regra, mono: true },
    { label: "Grupo", value: rule ? QUALITY_RULE_GROUP_LABELS[rule.group] : "—" },
    { label: "Projeto", value: projectLabel(issue.projeto, projectNames) },
    { label: "Detectada em", value: formatDetectedAt(issue.detectadaEm) },
    { label: "Registro afetado", value: rule?.entity ?? "—", mono: true },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 fade-in" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto slide-in-from-right shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-background-200/70 sticky top-0 bg-white z-10">
          <h3 className="text-sm font-semibold font-heading text-foreground-900">Detalhe da Inconsistência</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="p-4 space-y-5">
          <div className="flex items-center gap-2">
            <SeverityBadge severity={issue.severidade} />
            <ValidationStatusBadge status={issue.status} />
          </div>

          <h4 className="text-base font-semibold font-heading text-foreground-900">{rule?.name ?? issue.regra}</h4>
          <p className="text-sm text-foreground-600">{issue.descricao}</p>
          {rule && <p className="text-xs text-foreground-500">Regra: {rule.description}</p>}

          <div className="grid grid-cols-2 gap-3">
            {info.map((i) => (
              <div key={i.label} className="bg-background-50 rounded-card p-3 border border-background-200/70">
                <p className="text-xs text-foreground-500 mb-1">{i.label}</p>
                <p className={`text-sm font-medium text-foreground-800 break-all ${i.mono ? "font-mono" : ""}`}>{i.value}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-foreground-500">
            A inconsistência sai desta lista quando o registro deixa de violar a regra no banco. Não há fluxo de correção
            ou exceção pela tela ainda.
          </p>
        </div>
      </div>
    </div>
  );
}
